import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, Building2, Save, MessageSquare, Clock, 
  CheckCircle2, Crown, Building, Shield, Key, AlertCircle,
  Camera, Trash2, Upload, X, Globe
} from 'lucide-react';
import axios from 'axios';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const roleLabels = { 
  master_admin: 'Master Admin', 
  admin: 'Administrador', 
  tenant_admin: 'Admin da Empresa',
  manager: 'Gerente', 
  attendant: 'Atendente' 
};

function Profile() {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', password: '' });
  const [saving, setSaving] = useState(false);
  
  // ⭐ Estados para avatar
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  // ⭐ Estados para idioma
  const { t, language, setLanguage, supportedLanguages } = useLanguage();
  const [changingLanguage, setChangingLanguage] = useState(false);
  const [languageSuccess, setLanguageSuccess] = useState(false);

  // Estados para OTP
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  // ⭐ Auto-hide success message
  useEffect(() => {
    if (languageSuccess) {
      const timer = setTimeout(() => setLanguageSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [languageSuccess]);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data.user);
      setStats(res.data.stats);
      setForm({ 
        name: res.data.user.name, 
        phone: res.data.user.phone || '',
        password: ''
      });
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar perfil:', err);
      setLoading(false);
    }
  };

  // ============================================
  // ⭐ HANDLERS DE IDIOMA
  // ============================================
  const handleChangeLanguage = async (newLanguage) => {
    if (newLanguage === language) return;
    
    setChangingLanguage(true);
    try {
      const success = await setLanguage(newLanguage);
      if (success) {
        setLanguageSuccess(true);
      }
    } catch (err) {
      alert(t('common.error') + ': ' + (err.message || 'Erro ao alterar idioma'));
    } finally {
      setChangingLanguage(false);
    }
  };

  // ============================================
  // ⭐ HANDLERS DE AVATAR
  // ============================================
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamanho (máx 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 2MB');
      return;
    }

    // Validar tipo
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      alert('Apenas imagens JPG, PNG ou WEBP são permitidas');
      return;
    }

    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) return;

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/profile/avatar`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

            if (res.data.success) {
        alert('✅ Foto de perfil atualizada com sucesso!');
        setAvatarFile(null);
        setAvatarPreview(null);
        loadProfile();
        window.dispatchEvent(new Event('profile-updated')); // ⭐ NOVO: avisa o cabeçalho
      }
    } catch (err) {
      alert('Erro ao enviar foto: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Remover sua foto de perfil?')) return;

    try {
      const token = localStorage.getItem('token');
            await axios.delete(`${API}/api/profile/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Foto removida!');
      loadProfile();
      window.dispatchEvent(new Event('profile-updated')); // ⭐ NOVO: avisa o cabeçalho
    } catch (err) {
      alert('Erro ao remover foto: ' + (err.response?.data?.error || err.message));
    }
  };

  const cancelAvatarUpload = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  // ============================================
  // FLUXO DE ALTERAÇÃO DE SENHA COM OTP
  // ============================================
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (form.password && form.password.length > 0) {
      if (!otpVerified) {
        setShowOTPModal(true);
        return;
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const data = { name: form.name, phone: form.phone };
      if (form.password) {
        data.password = form.password;
        data.otp_code = otpCode;
      }
      
      await axios.put(`${API}/api/profile/me`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(t('profile.saveSuccess'));
      setEditing(false);
      setOtpVerified(false);
      setOtpCode('');
      setForm({...form, password: ''});
      loadProfile();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      alert(t('common.error') + ': ' + errorMsg);
      
      if (err.response?.data?.code === 'INVALID_OTP' || err.response?.data?.code === 'EXPIRED_OTP') {
        setOtpVerified(false);
        setOtpCode('');
      }
    } finally {
      setSaving(false);
    }
  };

  const requestOTP = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/otp/generate`, { purpose: 'change_password' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOtpCountdown(600);
      alert('✅ Código enviado para seu email!');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setOtpError(errorMsg);
      alert('❌ ' + t('common.error') + ': ' + errorMsg);
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOTP = async () => {
    if (otpCode.length !== 6) {
      setOtpError('Digite o código de 6 dígitos');
      return;
    }

    setOtpSending(true);
    setOtpError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/otp/verify`, { 
        code: otpCode, 
        purpose: 'change_password' 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOtpVerified(true);
      setShowOTPModal(false);
      alert('✅ Código verificado!');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setOtpError(errorMsg);
    } finally {
      setOtpSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('common.error')}</p>
        <button 
          onClick={loadProfile}
          className="mt-4 px-4 py-2 bg-green-600 text-white rounded-xl">
          Tentar Novamente
        </button>
      </div>
    );
  }

  const isMasterAdmin = profile.role === 'master_admin';
  const avatarUrl = profile.avatar_url ? `${API}${profile.avatar_url}` : null;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* ⭐ Mensagem de sucesso do idioma */}
      {languageSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-slideIn">
          <CheckCircle2 size={20} />
          <span className="font-medium">{t('profile.languageSuccess')}</span>
          <button onClick={() => setLanguageSuccess(false)} className="ml-2 hover:opacity-70">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <User size={28} className="text-green-600" />
            {t('profile.title')}
          </h1>
          <p className="text-gray-500">{t('profile.subtitle')}</p>
        </div>
        {!editing && (
          <button 
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 flex items-center gap-2">
            <Key size={16} />
            {t('profile.edit')}
          </button>
        )}
      </div>

      {/* Card Principal */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className={`p-8 text-white ${
          isMasterAdmin 
            ? 'bg-gradient-to-r from-yellow-500 to-orange-600'
            : 'bg-gradient-to-r from-green-500 to-green-600'
        }`}>
          <div className="flex items-center gap-6">
            {/* ⭐ ÁREA DO AVATAR */}
            <div className="relative group">
              <div className="w-28 h-28 bg-white/20 rounded-full flex items-center justify-center text-5xl font-bold backdrop-blur-sm overflow-hidden border-4 border-white/30">
                {avatarPreview ? (
                  <img 
                    src={avatarPreview} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : avatarUrl ? (
                  <img 
                    src={avatarUrl} 
                    alt={profile.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  profile.name.charAt(0).toUpperCase()
                )}
              </div>
              
              {/* Overlay com botão de câmera */}
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <Camera size={28} className="text-white" />
                <input 
                  type="file" 
                  accept="image/jpeg,image/png,image/jpg,image/webp"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </label>
            </div>

            <div className="flex-1">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                {profile.name}
                {isMasterAdmin && <Crown size={24} />}
              </h2>
              <p className="opacity-90">{profile.email}</p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold backdrop-blur-sm">
                  {roleLabels[profile.role]}
                </span>
                {profile.sector_name && (
                  <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold backdrop-blur-sm flex items-center gap-1">
                    <Building2 size={12} /> {profile.sector_name}
                  </span>
                )}
                {profile.tenant_name && (
                  <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold backdrop-blur-sm flex items-center gap-1">
                    <Building size={12} /> {profile.tenant_name}
                  </span>
                )}
                {/* ⭐ Badge do idioma atual */}
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold backdrop-blur-sm flex items-center gap-1">
                  <Globe size={12} />
                  {supportedLanguages.find(l => l.code === language)?.flag} {supportedLanguages.find(l => l.code === language)?.name}
                </span>
              </div>
            </div>
          </div>

          {/* ⭐ AÇÕES DO AVATAR */}
          {(avatarPreview || avatarUrl) && (
            <div className="flex gap-2 mt-4 justify-end">
              {avatarPreview && (
                <>
                  <button
                    onClick={cancelAvatarUpload}
                    className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium flex items-center gap-1.5 backdrop-blur-sm"
                  >
                    <X size={14} />
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleUploadAvatar}
                    disabled={uploadingAvatar}
                    className="px-3 py-1.5 bg-white text-gray-900 rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-gray-100 disabled:opacity-50"
                  >
                    {uploadingAvatar ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-900"></div>
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <Upload size={14} />
                        {t('common.save')}
                      </>
                    )}
                  </button>
                </>
              )}
              {avatarUrl && !avatarPreview && (
                <button
                  onClick={handleRemoveAvatar}
                  className="px-3 py-1.5 bg-red-500/80 hover:bg-red-500 rounded-lg text-xs font-medium flex items-center gap-1.5 backdrop-blur-sm"
                >
                  <Trash2 size={14} />
                  {t('common.delete')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Estatísticas */}
        {stats && (
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 p-6 border-b border-gray-100 ${
            isMasterAdmin ? 'bg-gradient-to-r from-yellow-50 to-orange-50' : ''
          }`}>
            {isMasterAdmin ? (
              <>
                <div className="text-center">
                  <div className="p-3 bg-blue-50 text-blue-600 rounded-xl inline-block mb-2">
                    <Building size={20} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_tenants || 0}</p>
                  <p className="text-xs text-gray-500">Total Empresas</p>
                </div>
                <div className="text-center">
                  <div className="p-3 bg-green-50 text-green-600 rounded-xl inline-block mb-2">
                    <CheckCircle2 size={20} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.active_tenants || 0}</p>
                  <p className="text-xs text-gray-500">Empresas Ativas</p>
                </div>
                <div className="text-center">
                  <div className="p-3 bg-purple-50 text-purple-600 rounded-xl inline-block mb-2">
                    <User size={20} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_users || 0}</p>
                  <p className="text-xs text-gray-500">Total Usuários</p>
                </div>
                <div className="text-center">
                  <div className="p-3 bg-orange-50 text-orange-600 rounded-xl inline-block mb-2">
                    <MessageSquare size={20} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {(stats.messages_30d || 0).toLocaleString('pt-BR')}
                  </p>
                  <p className="text-xs text-gray-500">Mensagens (30d)</p>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <div className="p-3 bg-blue-50 text-blue-600 rounded-xl inline-block mb-2">
                    <MessageSquare size={20} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_conversations || 0}</p>
                  <p className="text-xs text-gray-500">Total Conversas</p>
                </div>
                <div className="text-center">
                  <div className="p-3 bg-green-50 text-green-600 rounded-xl inline-block mb-2">
                    <CheckCircle2 size={20} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.closed_conversations || 0}</p>
                  <p className="text-xs text-gray-500">Resolvidas</p>
                </div>
                <div className="text-center">
                  <div className="p-3 bg-orange-50 text-orange-600 rounded-xl inline-block mb-2">
                    <Clock size={20} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.avg_response_minutes ? Math.round(stats.avg_response_minutes) : 0}m
                  </p>
                  <p className="text-xs text-gray-500">Tempo Médio</p>
                </div>
                <div className="text-center">
                  <div className="p-3 bg-purple-50 text-purple-600 rounded-xl inline-block mb-2">
                    <MessageSquare size={20} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.today_conversations || 0}</p>
                  <p className="text-xs text-gray-500">Hoje</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Informações */}
        <div className="p-6">
          {!editing ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <User size={20} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">{t('profile.name')}</p>
                  <p className="font-medium text-gray-900">{profile.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <Mail size={20} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">{t('profile.email')}</p>
                  <p className="font-medium text-gray-900">{profile.email}</p>
                </div>
              </div>
              {profile.phone && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <Phone size={20} className="text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">{t('profile.phone')}</p>
                    <p className="font-medium text-gray-900">{profile.phone}</p>
                  </div>
                </div>
              )}
              {profile.last_login && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <Clock size={20} className="text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">{t('profile.lastLogin')}</p>
                    <p className="font-medium text-gray-900">
                      {new Date(profile.last_login).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.name')}</label>
                <input 
                  type="text" 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})} 
                  required
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.phone')}</label>
                <input 
                  type="tel" 
                  value={form.phone} 
                  onChange={e => setForm({...form, phone: e.target.value})} 
                  placeholder="(00) 00000-0000"
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.password')} ({t('profile.passwordHint')})
                </label>
                <input 
                  type="password" 
                  value={form.password} 
                  onChange={e => {
                    setForm({...form, password: e.target.value});
                    if (e.target.value.length > 0) {
                      setOtpVerified(false);
                      setOtpCode('');
                    }
                  }} 
                  placeholder={t('profile.passwordPlaceholder')}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                />
                {form.password && form.password.length > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    {otpVerified ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                        <CheckCircle2 size={14} /> Código verificado
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-orange-600 font-medium">
                        <Shield size={14} /> Você precisará confirmar por email ao salvar
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* ⭐ SELETOR DE IDIOMA */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <label className="block text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <Globe size={18} className="text-blue-600" />
                  {t('profile.language')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {supportedLanguages.map(lang => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => handleChangeLanguage(lang.code)}
                      disabled={changingLanguage}
                      className={`p-3 border-2 rounded-xl text-sm font-medium transition-all ${
                        language === lang.code
                          ? 'border-green-500 bg-green-50 text-green-700 shadow-md'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                      } ${changingLanguage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span className="text-2xl block mb-1">{lang.flag}</span>
                      <p className="text-xs font-bold">{lang.name}</p>
                      {language === lang.code && (
                        <CheckCircle2 size={14} className="text-green-600 mx-auto mt-1" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-blue-700 mt-3 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {t('profile.languageHelp')}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => {
                    setEditing(false);
                    setOtpVerified(false);
                    setOtpCode('');
                    setForm({...form, password: ''});
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                  {t('common.cancel')}
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50">
                  <Save size={16} /> {saving ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Modal OTP */}
      {showOTPModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Shield size={28} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Verificação de Segurança</h2>
                  <p className="text-sm opacity-90">Confirme sua identidade</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-sm text-orange-900">
                  <strong>🔐 Por que precisamos disso?</strong><br/>
                  Para proteger sua conta, você precisa confirmar que é você mesmo ao alterar a senha.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>📧 Email de destino:</strong>
                </p>
                <p className="text-sm font-mono text-gray-900">{profile.email}</p>
              </div>

              {!otpVerified ? (
                <>
                  {!otpCountdown ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-700">
                        Deseja receber um código de verificação por email?
                      </p>
                      <button 
                        onClick={requestOTP}
                        disabled={otpSending}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold hover:from-orange-600 hover:to-red-700 disabled:opacity-50">
                        {otpSending ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Mail size={18} />
                            Enviar Código por Email
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Código enviado!</span>
                        <span className="text-orange-600 font-mono font-bold">
                          Expira em: {Math.floor(otpCountdown / 60)}:{String(otpCountdown % 60).padStart(2, '0')}
                        </span>
                      </div>
                      
                      <input 
                        type="text"
                        value={otpCode}
                        onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        className="w-full p-4 text-center text-3xl font-mono font-bold tracking-widest border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      />
                      
                      {otpError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
                          <AlertCircle size={16} />
                          {otpError}
                        </div>
                      )}

                      <button 
                        onClick={verifyOTP}
                        disabled={otpSending || otpCode.length !== 6}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold hover:from-green-600 hover:to-green-700 disabled:opacity-50">
                        {otpSending ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Verificando...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={18} />
                            Verificar Código
                          </>
                        )}
                      </button>

                      <button 
                        onClick={requestOTP}
                        disabled={otpSending}
                        className="w-full text-sm text-orange-600 hover:text-orange-700 font-medium py-2">
                        📧 Reenviar código
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 size={40} />
                  </div>
                  <p className="text-lg font-bold text-gray-900">Identidade Confirmada!</p>
                  <p className="text-sm text-gray-600 mt-2">Você já pode salvar a nova senha.</p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowOTPModal(false);
                  setOtpError('');
                }}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⭐ Animações CSS */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slideIn { animation: slideIn 0.3s ease; }
      `}</style>
    </div>
  );
}

export default Profile;
